/**
 * Gibt die URL nur zurueck, wenn sie ein http(s)-Schema hat — sonst null.
 * Schutz gegen javascript:/data:-URLs in href (Stored-XSS), v.a. bei
 * user-eingereichten Feldern wie portfolio_url im Admin-Review. Das
 * Backend erlaubt bewusst auch Social-Handles (@name)/nackte Domains
 * (blockt nur gefaehrliche Schemata) — DIESE Funktion ist die
 * Render-Sicherung: nur http(s) wird klickbarer Link, alles andere
 * rendert der Aufrufer als reinen Text.
 */
export const safeExternalHref = (url) => {
  if (typeof url !== 'string') return null;
  const trimmed = url.trim();
  return /^https?:\/\//i.test(trimmed) ? trimmed : null;
};

/**
 * Liest eine lesbare Fehlermeldung aus einem Axios-Error. FastAPI/Pydantic
 * liefern bei 422 `detail` als Array von {msg,...} statt String — sonst
 * landet "[object Object]" im Toast. Faellt auf `fallback` zurueck.
 */
export const extractApiError = (error, fallback = 'Etwas ist schiefgelaufen') => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail.length && detail[0]?.msg) {
    return detail[0].msg;
  }
  return fallback;
};

/**
 * Haptic feedback utility for mobile devices
 * @param {'light' | 'medium' | 'heavy' | 'success' | 'error'} type - Type of haptic feedback
 */
export const hapticFeedback = (type = 'light') => {
  if (!('vibrate' in navigator)) return;
  
  const patterns = {
    light: 10,
    medium: 20,
    heavy: [30, 10, 30],
    success: [10, 50, 10],
    error: [50, 30, 50, 30, 50],
  };
  
  navigator.vibrate(patterns[type] || 10);
};

/**
 * Format a number as currency
 * @param {number} amount - Amount to format
 * @param {string} currency - Currency code (default: USD)
 */
export const formatCurrency = (amount, currency = 'USD') => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(amount);
};

/**
 * Format a number with thousands separators
 * @param {number} num - Number to format
 */
export const formatNumber = (num) => {
  return new Intl.NumberFormat('en-US').format(num);
};

/**
 * Aktuelle Locale fuer Datums-/Zahlen-Formatierung.
 *
 * Reihenfolge:
 *  1. `localStorage.app_locale` -- explizite User-Wahl. Wird gesetzt
 *     sobald react-i18next im Schritt-2-Roadmap-Punkt landet; bis
 *     dahin koennen einzelne Settings-Toggles direkt hier schreiben.
 *  2. `navigator.language` -- Browser-Default vom OS abgeleitet.
 *  3. `en-US` als finaler Fallback.
 *
 * Aktuell unterstuetzt: `de-DE` und `en-US`. Andere Sprachen werden
 * auf `en-US` gemappt -- so haben wir wenigstens ein konsistentes
 * Format statt halb-uebersetzte Strings.
 */
export const getLocale = () => {
  try {
    const stored = (typeof localStorage !== 'undefined') ? localStorage.getItem('app_locale') : null;
    if (stored === 'de-DE' || stored === 'en-US') return stored;
  } catch { /* private mode / SSR */ }
  const browser = (typeof navigator !== 'undefined' && navigator.language) || 'en-US';
  return browser.toLowerCase().startsWith('de') ? 'de-DE' : 'en-US';
};

/**
 * Format a date as relative time (e.g., "2 hours ago" / "vor 2h").
 * Fallback bei > 7 Tagen ueber formatDate (locale-aware).
 * @param {string | Date} dateString - Date to format
 */
export const formatTimeAgo = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diff = (now - date) / 1000;
  const isDe = getLocale().startsWith('de');

  if (diff < 60) return isDe ? 'gerade eben' : 'Just now';
  if (diff < 3600) return isDe ? `vor ${Math.floor(diff / 60)}m` : `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return isDe ? `vor ${Math.floor(diff / 3600)}h` : `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return isDe ? `vor ${Math.floor(diff / 86400)}T` : `${Math.floor(diff / 86400)}d ago`;

  return date.toLocaleDateString(getLocale());
};

/**
 * Format a date for display (Date + Time). Locale-aware via getLocale().
 *
 * Default ist Datum+Uhrzeit -- die meisten Aufrufer im UI wollen
 * beides ("Bet placed: May 20, 2026, 05:35 PM" bzw. "20. Mai 2026,
 * 17:35"). Wenn nur Datum gewuenscht: options={ hour: undefined }
 * oder formatDateOnly() nehmen.
 *
 * @param {string | Date} dateString - Date to format
 * @param {object} options - Intl.DateTimeFormat options override
 */
export const formatDate = (dateString, options = {}) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleString(getLocale(), {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    ...options,
  });
};

/**
 * Date-only variante ohne Uhrzeit -- fuer Profile-Joined-Date u.ae.
 * Locale-aware.
 * @param {string | Date} dateString
 */
export const formatDateOnly = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleDateString(getLocale(), {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

/**
 * Date+Time fuer Detail-Ansichten. Identisch zu formatDate seit
 * formatDate jetzt auch Uhrzeit zeigt -- bleibt als Alias fuer
 * Klarheit an Stellen die explizit Datum+Uhrzeit dokumentieren.
 * @param {string | Date} dateString
 */
export const formatDateTime = (dateString) => formatDate(dateString);

/**
 * Truncate text with ellipsis
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Maximum length
 */
export const truncateText = (text, maxLength = 50) => {
  if (!text || text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

/**
 * Generate a random color from the chart colors palette
 * @param {number} index - Index in the palette
 */
export const getChartColor = (index) => {
  const colors = ['#10b981', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444', '#06b6d4', '#ec4899', '#84cc16'];
  return colors[index % colors.length];
};

/**
 * Calculate percentage
 * @param {number} value - Current value
 * @param {number} total - Total value
 */
export const calculatePercentage = (value, total) => {
  if (!total) return 0;
  return ((value / total) * 100).toFixed(1);
};

/**
 * Debounce function
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in ms
 */
export const debounce = (func, wait) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), wait);
  };
};

/**
 * Throttle function
 * @param {Function} func - Function to throttle
 * @param {number} limit - Time limit in ms
 */
export const throttle = (func, limit) => {
  let inThrottle;
  return (...args) => {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
};

/**
 * Deep clone an object
 * @param {object} obj - Object to clone
 */
export const deepClone = (obj) => {
  return JSON.parse(JSON.stringify(obj));
};

/**
 * Check if user is on mobile device
 */
export const isMobile = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

/**
 * Generate a unique ID
 */
export const generateId = () => {
  return Math.random().toString(36).substring(2) + Date.now().toString(36);
};

/**
 * Prediction- + Civic-Maerkte zu EINER gemischten Render-Liste
 * zusammenfuehren. Reihenfolge folgt der etablierten Logik, garantiert
 * aber sichtbare Durchmischung beider Typen:
 *   1. aktiver Boost (boosted_until in der Zukunft) ganz oben
 *   2. pro Status-Tier (open -> closed -> settled):
 *      a) Maerkte mit Wett-Volumen > 0 zuerst (Volumen absteigend)
 *      b) der Rest (Volumen 0) wird Prediction+Civic GLEICHMAESSIG
 *         verwoben (je Typ nach Aktualitaet) -- so klebt kein Typ
 *         zusammen, auch wenn die Daten schubweise erstellt wurden.
 * Erwartet bereits status/kategorie-gefilterte Eingaben. Rueckgabe:
 * Array von { type: 'prediction'|'civic', data }.
 */
export const mixMarketList = (prediction = [], civic = []) => {
  const now = Date.now();
  const isBoosted = (m) => !!(m && m.boosted_until && new Date(m.boosted_until).getTime() > now);
  const STATUS_ORDER = { open: 0, closed: 1, settled: 2 };
  const vol = (m) => m.volume || 0;
  const recency = (m) => new Date(m.created_at).getTime() || 0;

  // Kleinere Liste gleichmaessig in die groessere einstreuen (Reihen-
  // folge beider bleibt erhalten).
  const weave = (a, b) => {
    if (!a.length) return b;
    if (!b.length) return a;
    const [big, small] = a.length >= b.length ? [a, b] : [b, a];
    const out = [];
    const step = big.length / small.length;
    let si = 0, nextInsert = step;
    for (let i = 0; i < big.length; i++) {
      out.push(big[i]);
      while (si < small.length && (i + 1) >= nextInsert - 1e-9) {
        out.push(small[si++]);
        nextInsert += step;
      }
    }
    while (si < small.length) out.push(small[si++]);
    return out;
  };

  const all = [
    ...prediction.map((data) => ({ type: 'prediction', data })),
    ...civic.map((data) => ({ type: 'civic', data })),
  ];

  const boosted = all
    .filter((it) => isBoosted(it.data))
    .sort((x, y) => vol(y.data) - vol(x.data) || recency(y.data) - recency(x.data));
  const rest = all.filter((it) => !isBoosted(it.data));

  const result = [...boosted];
  for (const status of ['open', 'closed', 'settled']) {
    const tier = rest.filter((it) => it.data.status === status);
    if (!tier.length) continue;
    const withVol = tier
      .filter((it) => vol(it.data) > 0)
      .sort((x, y) => vol(y.data) - vol(x.data) || recency(y.data) - recency(x.data));
    const zero = tier.filter((it) => vol(it.data) === 0);
    const zeroPred = zero.filter((it) => it.type === 'prediction').sort((x, y) => recency(y.data) - recency(x.data));
    const zeroCiv = zero.filter((it) => it.type === 'civic').sort((x, y) => recency(y.data) - recency(x.data));
    result.push(...withVol, ...weave(zeroPred, zeroCiv));
  }
  // Unbekannte Status (Fallback) hinten anhaengen.
  const known = new Set(['open', 'closed', 'settled']);
  const other = rest.filter((it) => !known.has(it.data.status)).sort((x, y) => recency(y.data) - recency(x.data));
  return [...result, ...other];
};
